import React from "react";
import DesktopHeader from "../../components/DesktopHeader";
import { useAppDispatch, useAppSelector, useDebounce } from "../../app/hooks";
import { selectLabels } from "../../slices/language";
import DesktopBody from "../../components/DesktopBody";
import TableComponent from "../../components/TableComponent";
import { TableData } from "../../components/TableComponent/TableComponent";
import api from "../../api";
import { CircularProgress } from "@mui/material";
import { DesktopFiltersProps } from "../../components/DesktopFilters/DesktopFilters";
import DesktopDropView from "../../components/DesktopDropView";
import { selectUser } from "../../slices/user";
import NewUnitForm from "../../components/NewUnitForm";
import dayjs from "dayjs";
import { showError } from "../../utils/helpers";

const Units = () => {
  const labels = useAppSelector(selectLabels);
  const user = useAppSelector(selectUser);
  const dispatch = useAppDispatch();

  const headers = [
    { label: "ID", key: "ID" },
    { label: labels.name, key: labels.name },
    { label: labels.access_points, key: labels.access_points },
    { label: labels.users, key: labels.users },
  ];

  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [tableDataToDisplay, setTableDataToDisplay] =
    React.useState<TableData | null>(null);
  const [searchFilter, setSearchFilter] = React.useState<string>("");
  const [showEditUnit, setShowEditUnit] = React.useState<boolean>(false);
  const [newUnit, setNewUnit] = React.useState<boolean>(false);
  const [page, setPage] = React.useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [totalUnits, setTotalUnits] = React.useState<number>(0);
  const [selectedUnit, setSelectedUnit] = React.useState<number | null>(null);
  const [selectedUnitDocumentId, setSelectedUnitDocumentId] = React.useState<
    string | null
  >(null);
  const [unitsData, setUnitsData] = React.useState<any[]>([]);
  const [dataTable, setDataTable] = React.useState<any[]>([]);

  const debouncedSearchText = useDebounce(searchFilter, 500);

  const fixedPage = React.useMemo(() => {
    return page + 1;
  }, [page]);

  const propertyId = user?.property?.id;

  const currentDate = dayjs().format("DD-MM-YY");

  const selectedPropertyName = user?.property?.name;

  const filters: DesktopFiltersProps = {
    filterData: [
      {
        type: "search",
        value: searchFilter,
        setValue: setSearchFilter,
        disabled: isLoading,
        tooltip: "",
        function: () => {},
        array: [],
        size: "small",
        icon: "",
        label: "",
        multiDateValue: null,
        setMultiDateValue: () => {},
        filterWidth: 350,
      },
      {
        type: "function",
        value: "",
        setValue: () => {},
        disabled: isLoading,
        tooltip: labels.download_content,
        function: () => {},
        array: [],
        size: "small",
        icon: "fa-solid:file-download",
        label: "",
        multiDateValue: null,
        setMultiDateValue: () => {},
        downloadable: true,
        downloadData: dataTable,
        downloadHeader: headers,
        downloadFilename: `${labels.access_points}-${selectedPropertyName} ${currentDate}`,
      },
      {
        type: "function",
        value: "",
        setValue: () => {},
        disabled: isLoading,
        tooltip: "",
        function: () => setNewUnit(true),
        array: [],
        size: "small",
        icon: "fa6-solid:circle-plus",
        label: "",
        multiDateValue: null,
        setMultiDateValue: () => {},
      },
    ],
  };

  React.useEffect(() => {
    getUnits(debouncedSearchText);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [labels, fixedPage, rowsPerPage, debouncedSearchText, propertyId]);

  const getUnits = async (debouncedSearchText: string) => {
    if (!propertyId) return;
    try {
      setIsLoading(true);
      const response = await api.unitsRequest(
        Number(propertyId),
        fixedPage,
        rowsPerPage,
        debouncedSearchText,
      );
      if (response) {
        setUnitsData(response.data);
        const dataForDownloadableTable = response.data.map((item) => {
          return {
            ID: `${item?.id}`,
            [labels.name]: `${item?.name}`,
            [labels.access_points]: `${item?.access_points?.map(
              (item) => item?.name,
            )}`,
            [labels.users]: `${item?.users?.length}`,
          };
        });
        setDataTable(dataForDownloadableTable);
        const totalRows = response?.meta?.pagination?.total;
        setTotalUnits(totalRows);
        const dataForTable: TableData = {
          header: [
            {
              label: labels.name,
              isIcon: false,
              tooltipLabel: "",
            },
            {
              label: labels.access_points,
              isIcon: false,
              tooltipLabel: "",
            },
            {
              label: labels.users,
              isIcon: false,
              tooltipLabel: "",
            },
          ],
          body: response.data.map((item) => {
            return {
              row: item.id,
              cells: [
                {
                  label: [
                    {
                      mainLabel: item?.name,
                      tooltipLabel: "",
                    },
                  ],
                  isIcon: false,
                  display: "flex-dir-row",
                  isButton: false,
                },
                {
                  label: item?.access_points?.map((accessPoint) => {
                    return {
                      mainLabel: accessPoint?.name,
                      tooltipLabel: "",
                    };
                  }),
                  isIcon: false,
                  display: "flex-dir-col",
                  isButton: false,
                },
                {
                  label: item?.users?.map((unitUser) => {
                    return {
                      mainLabel: unitUser?.name,
                      tooltipLabel: "",
                    };
                  }),
                  isIcon: false,
                  display: "flex-dir-col",
                  isButton: false,
                },
              ],
            };
          }),
        };
        setTableDataToDisplay(dataForTable);
      }
    } catch (error: any) {
      console.error(error);
      const errorMessage = error?.response?.data?.error?.message;
      showError(dispatch, errorMessage ? errorMessage : labels.generic_error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="w-100">
      <DesktopHeader title={labels.units} />
      <DesktopBody hasFilters={true} filterData={filters.filterData}>
        <div className="w-100 h-100 px-8 pb-8 border-box">
          {isLoading ? (
            <div className="w-100 h-100 border-box flex align-items-center justify-content-center">
              <CircularProgress />
            </div>
          ) : (
            <TableComponent
              selectableRow={true}
              data={tableDataToDisplay}
              onPressRow={(id: number) => {
                setSelectedUnit(id);
                setSelectedUnitDocumentId(
                  unitsData.find((item) => item?.id === id)?.documentId ?? null,
                );
                setShowEditUnit(true);
              }}
              page={page}
              setPage={setPage}
              rowsPerPage={rowsPerPage}
              setRowsPerPage={setRowsPerPage}
              total={totalUnits}
              tableHeight={"calc(100vh - 188px)"}
              emptyMessage={labels.no_units_registered}
            />
          )}
        </div>
      </DesktopBody>
      <DesktopDropView
        showView={showEditUnit}
        setShowView={setShowEditUnit}
        content={
          <div className="flex h-100 border-box">
            <div className="h-100 border-box p-8 scroll">
              <NewUnitForm
                showForm={showEditUnit}
                setShowForm={setShowEditUnit}
                extraFunc={() => getUnits(debouncedSearchText)}
                selectedUnit={selectedUnit}
                selectedUnitDocumentId={selectedUnitDocumentId}
                isMobile={false}
              />
            </div>
          </div>
        }
        extraFunc={false}
        title={labels.edit_unit}
        onCLoseFunc={() => {
          setSelectedUnit(null);
          setSelectedUnitDocumentId(null);
        }}
      />
      <DesktopDropView
        showView={newUnit}
        setShowView={setNewUnit}
        content={
          <div className="flex h-100 border-box">
            <div className="h-100 border-box p-8 scroll">
              <NewUnitForm
                showForm={newUnit}
                setShowForm={setNewUnit}
                extraFunc={() => getUnits(debouncedSearchText)}
                isMobile={false}
              />
            </div>
          </div>
        }
        extraFunc={false}
        title={labels.new_unit}
      />
    </div>
  );
};

export default Units;
